import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { zoneLabel } from "@/lib/zones";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Bell, BellRing, Mail, MailCheck, MailX, Send, Check, RefreshCw } from "lucide-react";

const T = {
  en: { title: "Staff alerts", sub: "You're notified here and by email the moment a player logs the same pain zone 3 days in a row.", none: "No alert yet — your squad is pain-free.", markRead: "Mark all read", days: "days in a row", emailed: "emailed", notEmailed: "email pending", failed: "email failed", retry: "Retry", resent: "Email sent",
    emailLabel: "Alert email", emailPh: "staff@yourclub.com", save: "Save", test: "Send test email", testing: "Sending…", saved: "Saved", sent: "Test email sent to", viewPlayer: "View player" },
  fr: { title: "Alertes staff", sub: "Tu es notifié ici et par email dès qu'un joueur signale la même zone douloureuse 3 jours d'affilée.", none: "Aucune alerte — ton effectif est sans douleur.", markRead: "Tout marquer lu", days: "jours d'affilée", emailed: "email envoyé", notEmailed: "email en attente", failed: "email échoué", retry: "Renvoyer", resent: "Email envoyé",
    emailLabel: "Email des alertes", emailPh: "staff@tonclub.fr", save: "Enregistrer", test: "Envoyer un email test", testing: "Envoi…", saved: "Enregistré", sent: "Email test envoyé à", viewPlayer: "Voir le joueur" },
};

export const ClubNotifications = () => {
  const { lang } = useLang();
  const t = T[lang] || T.en;
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ unread: 0, items: [] });
  const [email, setEmail] = useState(user?.alerts_email || "");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/club/notifications").then((r) => { setData(r.data); window.dispatchEvent(new Event("pp:notifications")); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const markRead = async () => { await api.post("/club/notifications/read"); await load(); };
  const resend = async (id) => {
    try { await api.post(`/club/notifications/${id}/resend`); toast.success(t.resent); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    try { const r = await api.put("/club/settings", { alerts_email: email || null }); setUser({ ...user, alerts_email: r.data.alerts_email }); toast.success(t.saved); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Failed"); }
  };
  const test = async () => {
    setBusy(true);
    try { const r = await api.post("/club/notifications/test"); toast.success(`${t.sent} ${r.data.to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-5" data-testid="club-notifications">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {data.unread > 0 ? <BellRing className="w-5 h-5 text-[#FF2E54] animate-pulse" /> : <Bell className="w-5 h-5 text-slate-400" />}
          <div className="font-display font-bold uppercase text-lg">{t.title}</div>
          {data.unread > 0 && <span className="px-2 py-0.5 rounded-full bg-[#FF2E54] text-white text-[10px] font-bold" data-testid="club-notifications-unread">{data.unread}</span>}
        </div>
        {data.unread > 0 && <Button size="sm" variant="ghost" onClick={markRead} data-testid="club-notifications-read-btn" className="text-slate-400 text-xs"><Check className="w-3 h-3 mr-1" />{t.markRead}</Button>}
      </div>
      <p className="text-xs text-slate-400 mt-1">{t.sub}</p>

      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <Mail className="w-4 h-4 text-slate-500" />
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={user?.email || t.emailPh} data-testid="club-alert-email-input" className="flex-1 min-w-[200px] h-8 bg-[#090C10] border-[#1E2638] text-white text-xs" />
        <Button size="sm" onClick={save} data-testid="club-alert-email-save" className="h-8 bg-white/10 hover:bg-white/20 text-white text-xs uppercase">{t.save}</Button>
        <Button size="sm" onClick={test} disabled={busy} data-testid="club-alert-email-test" className="h-8 bg-[#00F076] hover:bg-[#00C963] text-black text-xs font-bold uppercase"><Send className="w-3 h-3 mr-1" />{busy ? t.testing : t.test}</Button>
      </div>

      <div className="mt-4 space-y-2 max-h-72 overflow-auto">
        {data.items.length === 0 && <div className="text-slate-500 text-sm" data-testid="club-notifications-empty">{t.none}</div>}
        {data.items.map((n) => (
          <div key={n.id} className={`rounded-lg border p-3 flex flex-wrap items-center gap-3 ${n.read ? "border-[#1E2638] bg-[#090C10]" : "border-[#FF2E54]/50 bg-[#FF2E54]/10"}`} data-testid={`club-notification-${n.id}`}>
            <div className="flex-1 min-w-[180px]">
              <div className="text-sm"><b>{n.player_name}</b> · <span className="text-[#FF2E54] font-semibold">{zoneLabel(n.zone, lang)}</span> · {n.streak} {t.days}</div>
              <div className="text-[10px] font-mono-tech uppercase text-slate-500 flex items-center gap-2">
                {n.team_name} · {new Date(n.created_at).toLocaleString()}
                <span className={`inline-flex items-center gap-1 ${n.email_sent ? "text-[#00F076]" : n.email_status === "failed" ? "text-[#FF2E54]" : "text-amber-400"}`} data-testid={`club-notification-email-${n.id}`}>
                  {n.email_sent ? <MailCheck className="w-3 h-3" /> : n.email_status === "failed" ? <MailX className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                  {n.email_sent ? t.emailed : n.email_status === "failed" ? t.failed : t.notEmailed}
                </span>
                {!n.email_sent && <button onClick={() => resend(n.id)} data-testid={`club-notification-resend-${n.id}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-[#00F076] normal-case"><RefreshCw className="w-3 h-3" />{t.retry}</button>}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => nav(`/club/players/${n.player_id}`)} data-testid={`club-notification-view-${n.id}`} className="text-[#00F076] text-xs h-7">{t.viewPlayer} →</Button>
          </div>
        ))}
      </div>
    </div>
  );
};
